import React, { useState, useRef, useCallback, useEffect } from 'react';
import { RefreshCw, ArrowDown, Loader2 } from 'lucide-react';
import { useRefresh } from '../context/RefreshContext';

const PullToRefresh = ({ children }) => {
  const { refreshPage, isRefreshing } = useRefresh();
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isTriggered, setIsTriggered] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  
  const PULL_THRESHOLD = 100; // Minimum pull distance to trigger refresh
  const MAX_PULL = 150; // Maximum pull distance

  const handleTouchStart = useCallback((e) => {
    // Only enable pull-to-refresh at the top of the page
    if (window.scrollY > 5) return;
    
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || window.scrollY > 5 || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    if (diff > 0) {
      // Apply resistance effect
      const resistance = Math.min(diff * 0.5, MAX_PULL);
      setPullDistance(resistance);
      
      // Prevent default scroll when pulling down
      if (resistance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    
    setIsPulling(false);
    
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsTriggered(true);
      // Keep the indicator visible while refreshing
      setPullDistance(80);
      
      // Trigger refresh
      await refreshPage(true);
      
      // Reset after refresh
      setTimeout(() => {
        setPullDistance(0);
        setIsTriggered(false);
      }, 500);
    } else {
      // Reset pull distance with animation
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, refreshPage, isRefreshing]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Use passive: false for touchmove to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const shouldRelease = pullDistance >= PULL_THRESHOLD;

  return (
    <div ref={containerRef} className="relative min-h-screen overflow-x-hidden">
      {/* Pull-to-refresh indicator */}
      <div 
        className="fixed left-0 right-0 flex justify-center z-[60] transition-all duration-300 ease-out"
        style={{ 
          top: `${Math.min(pullDistance - 60, 20)}px`,
          opacity: pullDistance > 20 ? 1 : 0,
          pointerEvents: 'none'
        }}
      >
        <div 
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all duration-300 ${
            isTriggered || isRefreshing
              ? 'bg-green-500 text-white' 
              : shouldRelease 
                ? 'bg-blue-500 text-white' 
                : 'bg-white text-gray-700 border border-gray-200'
          }`}
          style={{
            transform: `scale(${0.8 + progress * 0.2})`
          }}
        >
          {isTriggered || isRefreshing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Refreshing...</span>
            </>
          ) : shouldRelease ? (
            <>
              <RefreshCw className="w-5 h-5" />
              <span className="text-sm font-medium">Release to refresh</span>
            </>
          ) : (
            <>
              <ArrowDown 
                className="w-5 h-5 transition-transform duration-200" 
                style={{ transform: `rotate(${progress * 180}deg)` }}
              />
              <span className="text-sm font-medium">Pull down to refresh</span>
            </>
          )}
        </div>
      </div>

      {/* Content with pull effect */}
      <div 
        className="transition-transform duration-200 ease-out"
        style={{ 
          transform: isPulling && pullDistance > 0 ? `translateY(${pullDistance * 0.3}px)` : 'translateY(0)',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
